import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { Ticket, TicketPayload, TicketPrioridad, TicketEstado } from 'types/ticket.types';
import { ticketService } from 'services/ticketService';
import { getConfig } from 'utils/appConfig';

// ============================================================
// TicketsPage — Soporte técnico (ADMIN y DIRECTOR)
// ============================================================

const Ico = ({ d, cls = 'w-5 h-5' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className={cls}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);
const D_PLUS  = 'M12 4.5v15m7.5-7.5h-15';
const D_X     = 'M6 18 18 6M6 6l12 12';
const D_TICK  = 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z';
const D_SPIN  = 'M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99';

const Spinner = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

// ── Badges ───────────────────────────────────────────────────
const PRIORIDAD_CFG: Record<TicketPrioridad, { label: string; cls: string }> = {
  BAJA:  { label: 'Baja',  cls: 'bg-slate-100 text-slate-600' },
  MEDIA: { label: 'Media', cls: 'bg-amber-100 text-amber-700' },
  ALTA:  { label: 'Alta',  cls: 'bg-red-100 text-red-700'     },
};

const ESTADO_CFG: Record<TicketEstado, { label: string; cls: string }> = {
  ABIERTO:    { label: 'Abierto',     cls: 'bg-blue-100 text-blue-700'    },
  EN_PROCESO: { label: 'En proceso',  cls: 'bg-amber-100 text-amber-700'  },
  RESUELTO:   { label: 'Resuelto',    cls: 'bg-green-100 text-green-700'  },
  CERRADO:    { label: 'Cerrado',     cls: 'bg-slate-100 text-slate-500'  },
};

const Badge = ({ cls, label }: { cls: string; label: string }) => (
  <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${cls}`}>{label}</span>
);

// ── Formato de fecha ─────────────────────────────────────────
const fmtFecha = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('es-PE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// ── Estado vacío del formulario ──────────────────────────────
const FORM_EMPTY: TicketPayload = {
  titulo:      '',
  descripcion: '',
  prioridad:   'MEDIA',
  institucion: '',
};

// ============================================================
const TicketsPage: React.FC = () => {
  const { user } = useAuth();

  const [tickets,  setTickets]  = useState<Ticket[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [form,     setForm]     = useState<TicketPayload>(FORM_EMPTY);
  const [formErr,  setFormErr]  = useState<Partial<Record<keyof TicketPayload, string>>>({});
  const [saving,   setSaving]   = useState(false);
  const [saveOk,   setSaveOk]   = useState(false);

  const cargar = useCallback(async () => {
    if (!user) return;
    setLoading(true); setError(null);
    try {
      setTickets(await ticketService.listar(user.idUsuario, user.dni));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al cargar tickets');
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { cargar(); }, [cargar]);

  const openForm = () => {
    setForm({ ...FORM_EMPTY, institucion: getConfig().institucionNombre });
    setFormErr({});
    setSaveOk(false);
    setShowForm(true);
  };

  const handleChange = (field: keyof TicketPayload, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErr(prev => { const n = { ...prev }; delete n[field]; return n; });
  };

  const validate = (): boolean => {
    const e: typeof formErr = {};
    if (!form.titulo.trim())      e.titulo      = 'Requerido';
    if (!form.descripcion.trim()) e.descripcion = 'Requerido';
    if (!form.institucion.trim()) e.institucion = 'Requerido';
    setFormErr(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await ticketService.crear(form);
      setSaveOk(true);
      setShowForm(false);
      cargar();
    } catch (e: unknown) {
      setFormErr({ titulo: e instanceof Error ? e.message : 'Error al crear ticket' });
    } finally { setSaving(false); }
  };

  const inputCls = (err?: string) =>
    `w-full px-3 py-2 rounded-xl border text-sm text-slate-800 bg-white outline-none
     focus:ring-2 focus:ring-blue-500 transition-all
     ${err ? 'border-red-400' : 'border-slate-200'}`;

  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Soporte Técnico</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {tickets.length} ticket{tickets.length !== 1 ? 's' : ''} registrado{tickets.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button onClick={openForm}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white
                     font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm">
          <Ico d={D_PLUS} cls="w-4 h-4" /> Nuevo Ticket
        </button>
      </div>

      {/* Toast éxito */}
      {saveOk && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 text-green-700
                        rounded-2xl px-4 py-3 text-sm font-medium">
          <Ico d={D_TICK} cls="w-5 h-5 text-green-600 shrink-0" />
          Ticket creado correctamente. Nuestro equipo lo atenderá a la brevedad.
          <button onClick={() => setSaveOk(false)} className="ml-auto text-green-500 hover:text-green-700">
            <Ico d={D_X} cls="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Formulario de nuevo ticket */}
      {showForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-700 text-sm">Nuevo Ticket de Soporte</h3>
            <button onClick={() => setShowForm(false)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors">
              <Ico d={D_X} cls="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Título */}
            <div className="sm:col-span-2 flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                Título <span className="text-red-500">*</span>
              </label>
              <input type="text" value={form.titulo}
                onChange={e => handleChange('titulo', e.target.value)}
                placeholder="Ej: Problema con módulo de notas"
                className={inputCls(formErr.titulo)} />
              {formErr.titulo && <p className="text-xs text-red-500">{formErr.titulo}</p>}
            </div>

            {/* Prioridad */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Prioridad</label>
              <select value={form.prioridad}
                onChange={e => handleChange('prioridad', e.target.value)}
                className={inputCls()}>
                <option value="BAJA">Baja</option>
                <option value="MEDIA">Media</option>
                <option value="ALTA">Alta</option>
              </select>
            </div>

            {/* Institución */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                Institución <span className="text-red-500">*</span>
              </label>
              <input type="text" value={form.institucion}
                onChange={e => handleChange('institucion', e.target.value)}
                placeholder="Nombre de la institución"
                className={inputCls(formErr.institucion)} />
              {formErr.institucion && <p className="text-xs text-red-500">{formErr.institucion}</p>}
            </div>

            {/* Descripción */}
            <div className="sm:col-span-2 flex flex-col gap-1">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                Descripción <span className="text-red-500">*</span>
              </label>
              <textarea rows={4} value={form.descripcion}
                onChange={e => handleChange('descripcion', e.target.value)}
                placeholder="Describe el problema con el mayor detalle posible..."
                className={inputCls(formErr.descripcion) + ' resize-none'} />
              {formErr.descripcion && <p className="text-xs text-red-500">{formErr.descripcion}</p>}
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-1">
            <button onClick={() => setShowForm(false)} disabled={saving}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold
                         text-slate-600 hover:bg-slate-100 disabled:opacity-50 transition-colors">
              Cancelar
            </button>
            <button onClick={handleSubmit} disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-blue-700 hover:bg-blue-800
                         text-white text-sm font-bold transition-colors shadow-sm disabled:opacity-60">
              {saving ? <Spinner /> : <Ico d={D_TICK} cls="w-4 h-4" />}
              {saving ? 'Enviando…' : 'Enviar ticket'}
            </button>
          </div>
        </div>
      )}

      {/* Contenido */}
      {loading ? (
        <div className="flex justify-center items-center py-16 bg-white rounded-2xl border border-slate-100">
          <Spinner />
        </div>
      ) : error ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100 text-red-500 text-sm">{error}</div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.3}
            className="w-14 h-14 text-slate-300 mx-auto mb-4">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M16.712 4.33a9.027 9.027 0 0 1 1.652 1.306c.51.51.944 1.064 1.306 1.652M16.712 4.33l-3.448 4.138m3.448-4.138a9.014 9.014 0 0 0-9.424 0M19.67 7.288l-4.138 3.448m4.138-3.448a9.014 9.014 0 0 1 0 9.424m-4.138-5.976a3.736 3.736 0 0 0-.88-1.388 3.737 3.737 0 0 0-1.388-.88m2.268 2.268a3.765 3.765 0 0 1 0 2.528m-2.268-4.796a3.765 3.765 0 0 0-2.528 0m4.796 4.796c-.181.506-.475.982-.88 1.388a3.736 3.736 0 0 1-1.388.88m2.268-2.268 4.138 3.448m0 0a9.027 9.027 0 0 1-1.306 1.652c-.51.51-1.064.944-1.652 1.306m0 0-3.448-4.138m3.448 4.138a9.014 9.014 0 0 1-9.424 0m5.976-4.138a3.765 3.765 0 0 1-2.528 0m0 0a3.736 3.736 0 0 1-1.388-.88 3.737 3.737 0 0 1-.88-1.388m2.268 2.268L7.288 19.67m0 0a9.024 9.024 0 0 1-1.652-1.306 9.027 9.027 0 0 1-1.306-1.652m0 0 4.138-3.448M4.33 16.712a9.014 9.014 0 0 1 0-9.424m4.138 5.976a3.765 3.765 0 0 1 0-2.528m0 0c.181-.506.475-.982.88-1.388a3.736 3.736 0 0 1 1.388-.88m-2.268 2.268L4.33 7.288m6.406 1.18L4.33 7.288a9.026 9.026 0 0 1 1.652-1.652"/>
          </svg>
          <p className="text-slate-600 font-semibold">No tienes tickets registrados</p>
          <p className="text-slate-400 text-sm mt-1">Crea un ticket para reportar un problema o solicitar soporte</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map(t => (
            <div key={t.idTicket}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">

              {/* Fila superior */}
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge {...ESTADO_CFG[t.estado]} />
                  <Badge {...PRIORIDAD_CFG[t.prioridad]} />
                  <span className="text-xs text-slate-400">#{t.idTicket}</span>
                </div>
                <span className="text-xs text-slate-400 shrink-0">{fmtFecha(t.fechaCreacion)}</span>
              </div>

              {/* Título */}
              <h3 className="font-bold text-slate-800">{t.titulo}</h3>

              {/* Descripción */}
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{t.descripcion}</p>

              {/* Comentario de resolución */}
              {t.comentarioResolucion && (
                <div className="bg-green-50 border border-green-100 rounded-xl px-4 py-3">
                  <p className="text-xs font-bold text-green-700 mb-1">Respuesta del equipo de soporte</p>
                  <p className="text-sm text-green-800 whitespace-pre-wrap">{t.comentarioResolucion}</p>
                </div>
              )}

              {/* Pie */}
              <div className="flex items-center gap-3 text-xs text-slate-400 pt-1 border-t border-slate-100">
                <span>{t.institucion}</span>
                {t.fechaActualizacion !== t.fechaCreacion && (
                  <span className="ml-auto">Actualizado: {fmtFecha(t.fechaActualizacion)}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TicketsPage;
